/* eslint-disable import/no-named-as-default-member */
import React, { useEffect, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import usePackagesWithPaquers from 'redux/packagesWithPaquers';
import usePaquers from 'redux/paquers';
import { actions } from 'redux/packagesWithPaquers/slice';
import { actions as paquerActions } from 'redux/paquers/slice';
import { paquersItemsSelector } from 'redux/paquers/selectors';
import { selectGlobals } from 'redux/globals/selectors';
import {
  packagesWithPaquersItemsSelector,
  packagesWithPaquersPageableSelector,
  packagesWithPaquersLoadedSelector,
} from 'redux/packagesWithPaquers/selectors';
import dayjs from 'dayjs';
import PaqueryTable from '@paquery-team/lib-table';
import { DEFAULT_PAGINATE } from 'constants/defaultValues';
import SITELINKS from 'constants/sitelinks';
import { TableDateTimeFormat } from 'constants/dateFormats';
import ViewPackageButton from 'components/viewPackageButton';
import { authProfile } from 'redux/auth/selectors';
import externalCodeColumn from 'components/tables/columns';
import PaquerSettingsButton from 'components/paquerSettingsButton';
import SizeIndicator from 'components/sizeIndicator';
import EditRecordButton from 'components/editRecordButton';
import { useWithPaquersStatusOptions } from 'hooks/useOptions';

const destinationPersonNameColumn = {
  title: 'Nombre destinatario',
  dataIndex: 'destinationPersonName',
};

const destinationAddressColumn = {
  title: 'Destino',
  dataIndex: 'destinationAddress',
};

const deliveryTermColumn = {
  title: 'Plazo',
  dataIndex: 'deliveryTermDescription',
};

const arrivedAtPaqueryPointDateColumn = {
  title: 'Arribó a PaqueryPoint',
  dataIndex: 'arrivedAtPaqueryPointDate',
  align: 'center',
};

const statusColumn = {
  title: 'Estado',
  dataIndex: 'statusDescription',
};

const paquerypointColumn = {
  title: 'PaqueryPoint',
  dataIndex: 'paquerypoint',
  align: 'center',
};

const paquerColumn = {
  title: 'Paquer',
  dataIndex: 'paquer',
  align: 'center',
};

const sizeColumn = {
  title: 'Tamaño',
  dataIndex: 'packageSizeDescription',
  align: 'center',
  render: (text) => <SizeIndicator text={text[0]} />,
};

const editColumn = {
  align: 'center',
  render: (_, record) => (
    <EditRecordButton record={record} link={`${SITELINKS.packages.list}/id`} />
  ),
};

const viewPackageColumn = {
  align: 'center',
  render: (_, record) => <ViewPackageButton packageId={record.id} />,
};

const paquerSettingsColumn = {
  align: 'center',
  render: (_, record) => (
    <PaquerSettingsButton
      packageId={record}
      actions={actions}
      isWithPaquerListing
    />
  ),
};

const dataColumns = [
  externalCodeColumn,
  destinationPersonNameColumn,
  destinationAddressColumn,
  deliveryTermColumn,
  sizeColumn,
  arrivedAtPaqueryPointDateColumn,
  paquerypointColumn,
  statusColumn,
  paquerColumn,
];

const columnsLargeDevice = [
  ...dataColumns,
  paquerSettingsColumn,
  viewPackageColumn,
  editColumn,
];

const columnsSmallDevice = [
  externalCodeColumn,
  paquerSettingsColumn,
  viewPackageColumn,
  editColumn,
];

const columnsMediumDevice = [
  externalCodeColumn,
  statusColumn,
  paquerSettingsColumn,
  viewPackageColumn,
  editColumn,
];

const PackagesWithPaquers = () => {
  usePackagesWithPaquers();
  usePaquers();
  const dispatch = useDispatch();
  const { paquerypoint } = useSelector(authProfile);
  const globals = useSelector(selectGlobals);
  const items = useSelector(packagesWithPaquersItemsSelector);
  const pageable = useSelector(packagesWithPaquersPageableSelector);
  const paquers = useSelector(paquersItemsSelector);
  const loaded = useSelector(packagesWithPaquersLoadedSelector);
  const [paquerList, setPaquerList] = useState([]);
  const filteredStatus = useWithPaquersStatusOptions();

  useEffect(() => {
    dispatch(paquerActions.initial());
  }, [dispatch]);

  useEffect(() => {
    if (paquers.length > 0) {
      setPaquerList(
        paquers.map((paquer) => ({
          value: paquer.id,
          name: `${paquer.name} ${paquer.lastName}`,
        })),
      );
    }
  }, [paquers]);

  const searchPaquerCallback = useCallback(
    (newPaquerId) => {
      dispatch(actions.updatePaquerId(newPaquerId));
    },
    [dispatch],
  );
  const searchCallback = useCallback(
    (searchText) => {
      dispatch(actions.updateSearch(searchText));
    },
    [dispatch],
  );

  const updatePaginate = useCallback(
    (page) => {
      dispatch(actions.updatePageable(page));
    },
    [dispatch],
  );

  const selectCallback = useCallback(
    (value) => {
      dispatch(actions.updateStatus(value));
    },
    [dispatch],
  );

  const selectPackageTypeCallback = useCallback(
    (newPackageType) => {
      dispatch(actions.updatePackageType(newPackageType));
    },
    [dispatch],
  );

  const updateDate = useCallback(
    (value) => {
      dispatch(actions.updateDate(value));
    },
    [dispatch],
  );

  const selectors = [
    {
      onChange: searchPaquerCallback,
      placeholder: 'Paquer',
      list: paquerList,
    },
    {
      onChange: selectPackageTypeCallback,
      placeholder: 'Tipo de envio',
      list: globals.packages.type,
    },
    {
      onChange: selectCallback,
      placeholder: 'Estado',
      list: filteredStatus,
    },
  ];
  const searcher = {
    placeholder: 'Código externo',
    onSearching: searchCallback,
    allowEmptySearch: true,
  };
  const rangePicker = {
    onDateSelection: updateDate,
    required: {
      value: true,
      message: 'La fecha es requerida',
    },
  };

  let filteredPackages = [];
  if (loaded) {
    filteredPackages = items.map((packet) => ({
      ...packet,
      key: packet.id,
      externalCode: packet.externalCode,
      destinationPersonName: packet.shippingScheduleDestination.name,
      destinationAddress:
        packet.shippingScheduleDestination.shippingAddress.addressDetail,
      deliveryTermDescription: globals.deliveryTerm.find(
        (term) => term.value === packet.deliveryTerm,
      )?.description,
      packageSizeDescription: globals.packages.size.find(
        (packSize) => packSize.value === packet.packageSize,
      ).name,
      arrivedAtPaqueryPointDate: packet.arrivedAtPaqueryPointDate
        ? dayjs(packet.arrivedAtPaqueryPointDate)
            .tz()
            .format(TableDateTimeFormat)
        : null,
      // ponytail: los roles de OPL no tienen paquerypoint en el perfil; queda vacío
      paquerypoint: paquerypoint?.name ?? '',
      statusDescription: globals.packages.status.find(
        (state) => state.value === packet.status,
      ).name,
      paquer: packet.shippingScheduleDestination?.driver
        ? `${packet.shippingScheduleDestination.driver.name} ${packet.shippingScheduleDestination.driver.lastName} (${packet.shippingScheduleDestination.driver.id})`
        : '',
    }));
  }
  return (
    <PaqueryTable
      loading={!loaded}
      header={{
        searcher,
        selectors,
        rangePicker,
        refresh: () => dispatch(actions.refreshPage()),
        title: 'Paquetes por paquer',
      }}
      usePackageRowColors
      onChangePaginate={updatePaginate}
      dataSource={filteredPackages}
      paginate={pageable || DEFAULT_PAGINATE}
      dataColumns={dataColumns}
      colsForSmallDevice={columnsSmallDevice}
      colsForMediumDevice={columnsMediumDevice}
      colsForLargeDevice={columnsLargeDevice}
    />
  );
};

export default PackagesWithPaquers;
